// src/pages/tenant/TenantDetailPage.tsx
// Tenant detail — inline-edit property grid. There is no /edit route: click
// a value to edit it in place, commit on Enter/blur (autosave). See
// docs/ux/14-forms.md.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenantTenant, useI18n } from '@react/hooks';
import { Button, Badge, Alert, ErrorAlert } from '@react/components';
import { InlineEditText, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import type { Tenant } from '@core/types/tenant';

const TYPE_OPTIONS = [
  { value: 'TENANT_TYPE_PERSONAL', labelKey: 'tenant.typePersonal' },
  { value: 'TENANT_TYPE_TEAM', labelKey: 'tenant.typeTeam' },
  { value: 'TENANT_TYPE_ORGANIZATION', labelKey: 'tenant.typeOrganization' },
  { value: 'TENANT_TYPE_ENTERPRISE', labelKey: 'tenant.typeEnterprise' },
];

const ISOLATION_OPTIONS = [
  { value: 'TENANT_ISOLATION_LEVEL_SHARED', labelKey: 'tenant.isolationShared' },
  { value: 'TENANT_ISOLATION_LEVEL_DEDICATED_DB', labelKey: 'tenant.isolationDedicatedDb' },
  { value: 'TENANT_ISOLATION_LEVEL_DEDICATED', labelKey: 'tenant.isolationDedicated' },
];

const REGION_OPTIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'sa-east-1', label: 'South America (Sao Paulo)' },
];

function statusVariant(status: string | undefined): string {
  switch (status) {
    case 'TENANT_STATUS_ACTIVE':
      return 'success';
    case 'TENANT_STATUS_SUSPENDED':
      return 'error';
    case 'TENANT_STATUS_PENDING':
      return 'warning';
    case 'TENANT_STATUS_ARCHIVED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return '--';
  try {
    // Timestamp shape varies (ISO string or {seconds}); best-effort format.
    if (typeof ts === 'string') return new Date(ts).toLocaleString();
    return String(ts);
  } catch {
    return '--';
  }
}

/**
 * Tenant detail page — property grid with inline edit (docs/ux/14-forms.md
 * surface 2). Rare/destructive/multi-field configuration lives on the
 * dedicated settings sub-pages, linked from the quick-nav row.
 */
export const TenantDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenantTenant();
  const { t } = useI18n();
  const item = store.currentItem;
  const [savedField, setSavedField] = useState<string | null>(null);

  useEffect(() => {
    if (id) store.GetTenant(id).catch(() => {});
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  const commit = async (patch: Partial<Tenant>) => {
    if (!id || !item) return;
    try {
      await store.UpdateTenant(id, { tenant: { ...item, ...patch } });
      const field = Object.keys(patch)[0];
      setSavedField(field ?? null);
      setTimeout(() => setSavedField(null), 2000);
    } catch {
      // error surfaced via store.error
    }
  };

  const copySlug = () => {
    if (item?.slug) navigator.clipboard?.writeText(item.slug).catch(() => {});
  };

  if (store.isLoadingItem && !item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error && !item) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/tenants" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/tenants" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{item.name || t('common.untitled')}</span>
      </nav>

      {/* Header: inline-edit title + status */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <InlineEditText
              value={item.name}
              onCommit={(name) => commit({ name })}
              variant="title"
              aria-label={t('tenant.name')}
            />
            <Badge variant={statusVariant(item.status) as any} size="sm">
              {t(`tenant.status.${(item.status ?? '').replace('TENANT_STATUS_', '').toLowerCase()}`)}
            </Badge>
            {savedField && (
              <span className="text-xs text-muted-foreground" role="status">{t('tenant.saved')}</span>
            )}
          </div>
          <InlineEditText
            value={item.description}
            onCommit={(description) => commit({ description })}
            placeholder={t('tenant.descriptionPlaceholder')}
            allowEmpty
            aria-label={t('tenant.description')}
            className="mt-1"
          />
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Link to={`/tenants/${id}/dashboard`}>
            <Button variant="secondary" size="sm">{t('tenant.dashboard.title')}</Button>
          </Link>
          <Link to={`/tenants/${id}/members`}>
            <Button variant="secondary" size="sm">{t('tenant.members')}</Button>
          </Link>
          <Link to={`/tenants/${id}/settings`}>
            <Button variant="secondary" size="sm">{t('tenant.generalSettings')}</Button>
          </Link>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {item.suspended_at && (
        <Alert variant="warning">
          {t('tenant.danger.currentlySuspended')} —{' '}
          <Link to={`/tenants/${id}/settings/danger`} className="underline">
            {t('tenant.danger.title')}
          </Link>
        </Alert>
      )}

      {/* Property grid */}
      <div className="rounded-lg border border-border divide-y divide-border">
        <div className="px-4">
          <FormRow label={t('tenant.slug')} labelWidth="sm">
            <button
              type="button"
              onClick={copySlug}
              title={t('common.copy')}
              className="rounded px-1.5 py-0.5 font-mono text-sm text-foreground hover:bg-accent"
            >
              {item.slug || '--'}
            </button>
          </FormRow>
        </div>
        <div className="px-4">
          <FormRow label={t('tenant.type')} labelWidth="sm">
            <SegmentedControl
              aria-label={t('tenant.type')}
              size="sm"
              value={item.type ?? 'TENANT_TYPE_TEAM'}
              onChange={(v) => commit({ type: v as Tenant['type'] })}
              options={TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </FormRow>
        </div>
        <div className="px-4">
          <FormRow label={t('tenant.region')} labelWidth="sm">
            <div className="max-w-xs">
              <Select
                value={item.region ?? ''}
                onChange={(e) => commit({ region: e.target.value })}
                options={REGION_OPTIONS}
                aria-label={t('tenant.region')}
              />
            </div>
          </FormRow>
        </div>
        <div className="px-4">
          <FormRow label={t('tenant.isolationLevel')} labelWidth="sm">
            <SegmentedControl
              aria-label={t('tenant.isolationLevel')}
              size="sm"
              value={item.isolation_level ?? 'TENANT_ISOLATION_LEVEL_SHARED'}
              onChange={(v) => commit({ isolation_level: v as Tenant['isolation_level'] })}
              options={ISOLATION_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </FormRow>
        </div>
        <div className="px-4">
          <FormRow label={t('tenant.admin.fieldOwner')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">
              {item.owner?.display_name || item.owner?.entity?.id || '--'}
            </span>
          </FormRow>
        </div>
        <div className="px-4">
          <FormRow label={t('tenant.admin.fieldCreated')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">{formatTimestamp(item.created_at)}</span>
          </FormRow>
        </div>
        <div className="px-4">
          <FormRow label={t('tenant.admin.fieldUpdated')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">{formatTimestamp(item.updated_at)}</span>
          </FormRow>
        </div>
      </div>
    </div>
  );
});

export default TenantDetailPage;
